import React from 'react';
import { standardCosts } from '../../data/costAccountingData';

const StandardCostsView = () => {
  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">標準成本</h2>
        <p className="text-gray-600">Standard Costs</p>
      </div>

      {/* Standard Costs header image */}
      <div className="relative h-48 mb-6 overflow-hidden rounded-lg shadow-md">
        <img 
          src="https://images.unsplash.com/photo-1543286386-2e659306cd6c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
          alt="Standard Costs" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <h1 className="text-white text-3xl font-bold">標準成本管理</h1>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">產品</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">生效日期</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">直接材料</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">直接人工</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">製造費用</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">總標準成本</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {standardCosts.map((cost) => (
                <tr key={cost.standard_cost_id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{cost.product_name}</div>
                    <div className="text-sm text-gray-500">{cost.product_code}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{new Date(cost.effective_from).toLocaleDateString()}</div>
                    <div className="text-sm text-gray-500">至 {new Date(cost.effective_to).toLocaleDateString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${cost.direct_material_standard.toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${cost.direct_labor_standard.toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${cost.manufacturing_overhead_standard.toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">${cost.total_standard_cost.toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${cost.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {cost.is_active ? '活躍' : '非活躍'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">標準成本分佈</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {standardCosts.map((cost) => (
            <div key={cost.standard_cost_id} className="border rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h4 className="text-md font-medium text-gray-900">{cost.product_name}</h4>
                  <p className="text-sm text-gray-500">{cost.product_code}</p>
                </div>
                <div className="text-xl font-bold text-gray-900">${cost.total_standard_cost.toFixed(2)}</div>
              </div>
              
              <div className="relative pt-1">
                <div className="flex mb-2 items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full bg-blue-100 text-blue-800">
                      直接材料
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold inline-block text-blue-800">
                      {((cost.direct_material_standard / cost.total_standard_cost) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
                  <div style={{ width: `${(cost.direct_material_standard / cost.total_standard_cost) * 100}%` }} 
                       className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500">
                  </div>
                </div>
              </div>
              
              <div className="relative pt-1">
                <div className="flex mb-2 items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full bg-yellow-100 text-yellow-800">
                      直接人工
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold inline-block text-yellow-800">
                      {((cost.direct_labor_standard / cost.total_standard_cost) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
                  <div style={{ width: `${(cost.direct_labor_standard / cost.total_standard_cost) * 100}%` }} 
                       className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-yellow-500">
                  </div>
                </div>
              </div>
              
              <div className="relative pt-1">
                <div className="flex mb-2 items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full bg-purple-100 text-purple-800">
                      製造費用
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold inline-block text-purple-800">
                      {((cost.manufacturing_overhead_standard / cost.total_standard_cost) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
                  <div style={{ width: `${(cost.manufacturing_overhead_standard / cost.total_standard_cost) * 100}%` }} 
                       className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500">
                  </div>
                </div>
              </div>
              
              <div className="text-xs text-gray-500 mt-2">{cost.notes}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StandardCostsView;
